import { DOCUMENT } from '@angular/common';
import {
  DestroyRef,
  inject,
  Injectable,
  Signal,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  concat,
  defer,
  first,
  fromEvent,
  map,
  Observable,
  of,
  retry,
  switchMap,
  throwError,
  timer,
} from 'rxjs';
import type { BdJobChainResponse, BdJobChainSubject } from './chain.model';
import { BdJobChainApi, BdJobSystemUnavailableError } from './job-chain.api';

/**
 * - `locating`: looking up the chain of the subject.
 * - `running`: the chain is OPEN and polled.
 * - `closed`: the chain ended SUCCESS or FAILED; polling stopped.
 * - `unavailable`: no chain status — job system down, no chain found, or
 *   requests kept failing. Show the row's own status instead.
 */
export type BdJobChainTrackState =
  | 'locating'
  | 'running'
  | 'closed'
  | 'unavailable';

export interface BdJobChainHandle {
  /** The newest poll of the chain; `null` until it is found. */
  readonly chain: Signal<BdJobChainResponse | null>;
  readonly state: Signal<BdJobChainTrackState>;
}

export interface BdJobChainTrackOptions {
  /** Stops tracking; the tracker itself lives at the root. */
  destroyRef: DestroyRef;
  /** Delays between polls in ms; the last one repeats. */
  pollDelays?: readonly number[];
  /** Subject lookups before giving up on finding the chain. */
  locateAttempts?: number;
}

const DEFAULT_POLL_DELAYS: readonly number[] = [
  2_000, 2_000, 3_000, 5_000, 10_000, 15_000,
];
const DEFAULT_LOCATE_ATTEMPTS = 5;
/** Failed polls in a row before the status counts as unavailable. */
const POLL_RETRIES = 3;

function pollDelayAt(
  options: Pick<BdJobChainTrackOptions, 'pollDelays'>,
): (index: number) => number {
  const delays = options.pollDelays ?? DEFAULT_POLL_DELAYS;
  return (index) => delays[Math.min(index, delays.length - 1)] ?? 0;
}

class BdJobChainNotFoundError extends Error {
  constructor(subject: BdJobChainSubject) {
    super(`no chain for ${subject.type} ${subject.id}`);
  }
}

/**
 * Follows the chain a start endpoint kicked off. The start endpoints return
 * the row, not the chain, so the chain is found by the row's subject and then
 * polled by id until it closes — slower over time, and not while the tab is
 * hidden.
 */
@Injectable({ providedIn: 'root' })
export class BdJobChainTracker {
  private readonly _api = inject(BdJobChainApi);
  private readonly _document = inject(DOCUMENT);

  public trackSubject(
    subject: BdJobChainSubject,
    options: BdJobChainTrackOptions,
  ): BdJobChainHandle {
    const delayAt = pollDelayAt(options);
    return this._follow(
      this._locate(
        subject,
        options.locateAttempts ?? DEFAULT_LOCATE_ATTEMPTS,
        delayAt,
      ),
      delayAt,
      options,
    );
  }

  /**
   * Follows a chain already known by id — one that `findBySubject` listed,
   * e.g. the open chains of a clinic. Starts in `locating` until the first
   * poll answers.
   */
  public trackChain(
    id: string,
    options: Omit<BdJobChainTrackOptions, 'locateAttempts'>,
  ): BdJobChainHandle {
    return this._follow(of(id), pollDelayAt(options), options);
  }

  private _follow(
    id$: Observable<string>,
    delayAt: (index: number) => number,
    options: Pick<BdJobChainTrackOptions, 'destroyRef'>,
  ): BdJobChainHandle {
    const chain = signal<BdJobChainResponse | null>(null);
    const state = signal<BdJobChainTrackState>('locating');

    id$
      .pipe(
        switchMap((id) => this._poll(id, delayAt, 0)),
        takeUntilDestroyed(options.destroyRef),
      )
      .subscribe({
        next: (next) => {
          chain.set(next);
          state.set(next.status === 'OPEN' ? 'running' : 'closed');
        },
        error: () => state.set('unavailable'),
      });

    return { chain: chain.asReadonly(), state: state.asReadonly() };
  }

  /**
   * The newest chain of the subject, whatever its status: a short chain can
   * close before the first lookup, and filtering on OPEN would miss it.
   */
  private _locate(
    subject: BdJobChainSubject,
    attempts: number,
    delayAt: (index: number) => number,
  ): Observable<string> {
    return defer(() => this._api.findBySubject(subject, { limit: 1 })).pipe(
      map(([newest]) => {
        if (!newest) {
          throw new BdJobChainNotFoundError(subject);
        }
        return newest.id;
      }),
      retry({
        count: Math.max(attempts - 1, 0),
        delay: (error: unknown, retryCount) =>
          this._retryDelay(error, delayAt(retryCount - 1)),
      }),
    );
  }

  private _poll(
    id: string,
    delayAt: (index: number) => number,
    index: number,
  ): Observable<BdJobChainResponse> {
    return defer(() => this._api.get(id)).pipe(
      retry({
        count: POLL_RETRIES,
        delay: (error: unknown, retryCount) =>
          this._retryDelay(error, delayAt(index + retryCount)),
      }),
      switchMap((chain) =>
        chain.status === 'OPEN'
          ? concat(
              of(chain),
              this._wait(delayAt(index)).pipe(
                switchMap(() => this._poll(id, delayAt, index + 1)),
              ),
            )
          : of(chain),
      ),
    );
  }

  /** A down job system will not come back within a retry; anything else may. */
  private _retryDelay(error: unknown, ms: number): Observable<unknown> {
    return error instanceof BdJobSystemUnavailableError
      ? throwError(() => error)
      : this._wait(ms);
  }

  /** Waits `ms`, then until the tab is visible. */
  private _wait(ms: number): Observable<unknown> {
    return timer(ms).pipe(switchMap(() => this._visible()));
  }

  private _visible(): Observable<unknown> {
    const document = this._document;
    return document.visibilityState === 'hidden'
      ? fromEvent(document, 'visibilitychange').pipe(
          first(() => document.visibilityState !== 'hidden'),
        )
      : of(true);
  }
}
